import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { catchError, first, tap } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthNewService } from "src/auth/services/auth-new.service";
import { EMPTY } from "rxjs";
import { emailPattern, passwordPattern } from "src/app/app.constants";
import {
  normalizeMfaEmail,
  PENDING_MFA_STORAGE_KEY,
  PendingMfaState,
} from "src/auth/pending-mfa-state";

@Component({
  selector: "app-sign-in",
  templateUrl: "./sign-in.component.html",
  styleUrls: ["./sign-in.component.scss"],
})
export class SignInComponent implements OnInit {
  loading = false;
  @ViewChild("password") passwordInput: ElementRef<HTMLInputElement>;
  errorMessage: string;
  disableLogin = false;

  passwordType = "password";

  form: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    private auth: AuthNewService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.form = this.fb.group({
      username: [
        "",
        { validators: [Validators.required, Validators.pattern(emailPattern)] },
      ],
      password: [
        "",
        [Validators.required, Validators.pattern(passwordPattern)],
      ],
    });
  }

  get isDisabled() {
    return (
      this.form.controls.username?.invalid ||
      this.form.controls.password?.invalid
    );
  }

  ngOnInit(): void {
    this.form.controls.password.valueChanges.subscribe(() => {
      this.errorMessage = undefined;
    });
  }

  onSubmit() {
    if (this.form.invalid) return;

    const { username, password } = this.form.value;
    const pendingMfaState: PendingMfaState = {
      otpId: "",
      userId: 0,
      email: normalizeMfaEmail(username),
      redirectUrl: this.route.snapshot.queryParams["redirectUrl"] || null,
      maskedEmail: this.maskEmail(username),
    };

    this.clearPendingMfa();
    this.loading = true;

    this.auth
      .signIn(username, password)
      .pipe(
        first(),
        tap((otpResponse) => {
          pendingMfaState.otpId = otpResponse.otpId;
          pendingMfaState.userId = otpResponse.userId;
          this.writePendingMfa(pendingMfaState);

          this.router.navigate(["/auth/verify-otp"], {
            state: pendingMfaState,
          });
        }),
        catchError((e) => {
          console.log("error ", e);
          this.errorMessage = e.message;
          if (e.message === "Invalid password or email address") {
            this.errorMessage = "Incorrect email or password";
          }
          if (e.message === "too many login attempts") {
            this.disableLogin = true;
            this.errorMessage = "Too many attempts, please try later";
          }

          this.loading = false;

          return EMPTY;
        }),
      )
      .subscribe();
  }

  onTypeChange(e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    switch (this.passwordType) {
      case "password":
        return (this.passwordType = "text");
      case "text":
        return (this.passwordType = "password");
      default: {
        return (this.passwordType = "password");
      }
    }
  }

  private maskEmail(email: string): string {
    const [localPart, domain] = (email || "").split("@");

    if (!localPart || !domain) {
      return "your email address";
    }

    return `${localPart.slice(0, 3)}*****@${domain}`;
  }

  private writePendingMfa(state: PendingMfaState) {
    sessionStorage.setItem(PENDING_MFA_STORAGE_KEY, JSON.stringify(state));
  }

  private clearPendingMfa() {
    sessionStorage.removeItem(PENDING_MFA_STORAGE_KEY);
  }
}
